import { Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { BillingApiService, UpdatePricingPlanResponse } from '@vendasta/billing';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillingStrategy, billingStrategyToAPI } from './billing-strategy';
import { Distribution, PricingPlan, pricingPlanFromAPI } from './pricing-plan';

@Injectable({ providedIn: 'root' })
export class PricingPlanService {
  constructor(
    private environmentService: EnvironmentService,
    private billingService: BillingApiService,
  ) {}

  list(distribution?: Distribution): PricingPlan[] {
    const env = this.environmentService.getEnvironment();
    switch (distribution) {
      case Distribution.Retail:
        return retailPlans.get(env) || [];
      case Distribution.Wholesale:
        return wholesalePlans.get(env) || [];
      default:
        return pricingPlans.get(env) || [];
    }
  }

  isDefault(pricingPlanId: string): boolean {
    const defaultPlan = this.list().find((p) => p.name === WholesalePricingPlanName.Default);
    return defaultPlan ? pricingPlanId === defaultPlan.id : false;
  }

  nameForId(pricingPlanId: string): string {
    return this.list().find((p) => p.id === pricingPlanId)?.name || '';
  }

  idForName(pricingPlanName: string): string {
    return this.list().find((p) => p.name === pricingPlanName)?.id || '';
  }

  idForSubscriptionName(subscriptionName: string): string {
    return this.list().find((p) => p.subscriptionName === subscriptionName)?.id || '';
  }

  setDefaultStrategy(pricingPlanId: string, strategy: BillingStrategy): Observable<UpdatePricingPlanResponse> {
    const request = {
      id: pricingPlanId,
      fieldMask: {
        paths: ['default_billing_strategy'],
      },
      defaultBillingStrategy: billingStrategyToAPI(strategy),
    };
    return this.billingService.updatePricingPlan(request);
  }

  getMulti(pricingPlanIds: string[]): Observable<PricingPlan[]> {
    return this.billingService
      .getMultiPricingPlan({ pricingPlanIds })
      .pipe(map((resp) => resp.plans.map((p) => pricingPlanFromAPI(p))));
  }
}

enum RetailPricingPlanName {
  MSRP = 'MSRP',
}

const demoRetailPricingPlans: PricingPlan[] = [
  {
    id: 'PP-5049b4b2-fe28-473f-96d6-84d4ea91ada9',
    name: RetailPricingPlanName.MSRP,
    subscriptionName: 'msrp',
    defaultStrategy: BillingStrategy.Invalid,
  },
];

const prodRetailPricingPlans: PricingPlan[] = [
  {
    id: 'PP-148b6d8c-6222-4fce-9cd2-f0587f0db563',
    name: RetailPricingPlanName.MSRP,
    subscriptionName: 'msrp',
    defaultStrategy: BillingStrategy.Invalid,
  },
];

enum WholesalePricingPlanName {
  // Instant
  VBPFree = 'Free',
  VBPStarter = 'Starter',
  VBPBasic = 'Basic+',
  VBPProfessional = 'Professional+',
  VBPEnterprise = 'Enterprise+',
  VBPEnterprisePilot = 'Enterprise+ Pilot',
  VBP2Startup = 'Startup+',
  VBP2Essentials = 'Essentials+',
  VBP2Growth = 'Growth+',
  VBP2Scale = 'Scale+',

  // Invoiced
  BasicSubscription = 'Basic',
  ProfessionalSubscription = 'Professional',
  EnterpriseSubscription = 'Enterprise',
  EliteSubscription = 'Elite',
  PremiumSubscription = 'Premium (Legacy)',
  VBP2Premium = 'Premium',

  IndividualSubscription = 'Individual',
  TeamSubscription = 'Team',
  AdvanceSubscription = 'Advance',
  CustomSubscription = 'Custom',

  Essentials2022Subscription = 'Essentials',
  Professional2022Subscription = 'Professional',
  Premium2022Subscription = 'Premium',
  Custom2022Subscription = 'Custom',

  Starter2023Subscription = 'Starter',

  Starter2024Subscription = 'Starter 2024',
  Professional2024Subscription = 'Professional 2024',
  Premium2024Subscription = 'Premium 2024',

  Default = 'Default',
}

const demoWholesalePricingPlans: PricingPlan[] = [
  {
    id: 'PP-587f8f25-b278-4727-b7c4-3ce60e542c8b',
    name: WholesalePricingPlanName.VBPFree,
    subscriptionName: 'free_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-a5ad2825-8225-4bbe-a868-c1b2a7d2a2e3',
    name: WholesalePricingPlanName.VBPStarter,
    subscriptionName: 'starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-438ccc87-eaff-4180-ad79-f5d3d51e1bc0',
    name: WholesalePricingPlanName.VBPBasic,
    subscriptionName: 'vbp_basic_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-7201f892-3d44-44b8-b9db-4b23dd34d112',
    name: WholesalePricingPlanName.VBPProfessional,
    subscriptionName: 'vbp_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-3cc1ec9f-17a8-46c8-9e79-b337e4ea651b',
    name: WholesalePricingPlanName.VBPEnterprise,
    subscriptionName: 'vbp_enterprise_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-7ffa4745-4ea1-4603-983b-81a1404faa3b',
    name: WholesalePricingPlanName.VBPEnterprisePilot,
    subscriptionName: 'vbp_enterprise_pilot_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-02895bb5-ff6b-4f37-ae1e-8473b895a59d',
    name: WholesalePricingPlanName.VBP2Startup,
    subscriptionName: 'vbp2_startup_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-c69f1724-d380-432f-8f50-5778da6ea250',
    name: WholesalePricingPlanName.VBP2Essentials,
    subscriptionName: 'vbp2_essentials_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-8abe2c53-be4a-43d5-bd2c-a8d9be1c82db',
    name: WholesalePricingPlanName.VBP2Growth,
    subscriptionName: 'vbp2_growth_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-0a872f89-e582-420a-90b0-c98e19b174a4',
    name: WholesalePricingPlanName.VBP2Scale,
    subscriptionName: 'vbp2_scale_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-7abd1cb2-317b-40d2-9fdc-d2b365d645b7',
    name: WholesalePricingPlanName.VBP2Premium,
    subscriptionName: 'vbp2_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-a7eca7c3-84fd-4fc2-86a5-87a743f6c3d1',
    name: WholesalePricingPlanName.BasicSubscription,
    subscriptionName: 'basic_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-7d0053b8-fab4-407d-ae69-33c67d5314eb',
    name: WholesalePricingPlanName.ProfessionalSubscription,
    subscriptionName: 'professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-e85cdb56-2944-4fe0-9e78-a98520eb1489',
    name: WholesalePricingPlanName.EnterpriseSubscription,
    subscriptionName: 'enterprise_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-9a0d6430-5e73-41d2-bd3d-dc3645664bb9',
    name: WholesalePricingPlanName.EliteSubscription,
    subscriptionName: 'elite_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-a03a1c1e-d03e-4c77-8132-34908012a65c',
    name: WholesalePricingPlanName.PremiumSubscription,
    subscriptionName: 'premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-6ef8b121-c6de-49c2-b24f-971cfecb5fe6',
    name: WholesalePricingPlanName.IndividualSubscription,
    subscriptionName: 'm3_solo_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-ffd605c1-feb6-4066-8dc1-ad0f1a8efb9d',
    name: WholesalePricingPlanName.TeamSubscription,
    subscriptionName: 'm3_small_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-246b1485-018c-4dc2-96c1-b6e0b2c18edb',
    name: WholesalePricingPlanName.AdvanceSubscription,
    subscriptionName: 'm3_medium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-0e9f5ea4-d6bd-4ea8-927e-de55134fa92f',
    name: WholesalePricingPlanName.CustomSubscription,
    subscriptionName: 'm3_large_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-268f67e8-1854-4cdd-97a5-4e8b2c52a957',
    name: WholesalePricingPlanName.Essentials2022Subscription,
    subscriptionName: 'vbp_2022_essentials_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-231bb4f5-ab25-4570-a342-6c2548494fbb',
    name: WholesalePricingPlanName.Professional2022Subscription,
    subscriptionName: 'vbp_2022_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-88e09af4-137f-4b5f-a860-8cfc30675dd5',
    name: WholesalePricingPlanName.Premium2022Subscription,
    subscriptionName: 'vbp_2022_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-073e3bc9-121f-4fb9-b30f-9e6e48783be8',
    name: WholesalePricingPlanName.Custom2022Subscription,
    subscriptionName: 'vbp_2022_custom_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-2559d108-f406-43f6-b04c-36463f3b1935',
    name: WholesalePricingPlanName.Starter2023Subscription,
    subscriptionName: 'vbp_2023_starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-965650c8-5522-47b8-a4e6-c7b199f12b0e',
    name: WholesalePricingPlanName.Starter2024Subscription,
    subscriptionName: 'vbp_2024_starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-62079baa-89c3-422d-853b-27640e67c457',
    name: WholesalePricingPlanName.Professional2024Subscription,
    subscriptionName: 'vbp_2024_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-35011675-48d6-4bf1-a5aa-47e344541ec3',
    name: WholesalePricingPlanName.Premium2024Subscription,
    subscriptionName: 'vbp_2024_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-cf943a15-3eea-40cf-8d3e-e7ea5a5a9817',
    name: WholesalePricingPlanName.Default,
    subscriptionName: '',
    defaultStrategy: BillingStrategy.Invalid,
  },
];

const prodWholesalePricingPlans: PricingPlan[] = [
  {
    id: 'PP-77912c15-00af-4a56-bfc0-e8e6c27c7a53',
    name: WholesalePricingPlanName.VBPFree,
    subscriptionName: 'free_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-1cc75309-ddff-4d8b-aedf-88a7472e2b4e',
    name: WholesalePricingPlanName.VBPStarter,
    subscriptionName: 'starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-52f98211-bd1e-4112-88cd-e78040173198',
    name: WholesalePricingPlanName.VBPBasic,
    subscriptionName: 'vbp_basic_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-c278bcc6-bd5d-43e5-9ff5-a27e70b48ba9',
    name: WholesalePricingPlanName.VBPProfessional,
    subscriptionName: 'vbp_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-4133fc94-a58a-4fcd-91cd-9ed111182e77',
    name: WholesalePricingPlanName.VBPEnterprise,
    subscriptionName: 'vbp_enterprise_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-dbbac61e-132f-468f-aa62-7a691b59bb74',
    name: WholesalePricingPlanName.VBPEnterprisePilot,
    subscriptionName: 'vbp_enterprise_pilot_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-45f8099f-d3f3-4302-83bf-bbf64f431e31',
    name: WholesalePricingPlanName.VBP2Startup,
    subscriptionName: 'vbp2_startup_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-d44c5a8b-f3b6-4b63-b203-8b7253060984',
    name: WholesalePricingPlanName.VBP2Essentials,
    subscriptionName: 'vbp2_essentials_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-c1761320-62df-41d7-8df3-008554a4b592',
    name: WholesalePricingPlanName.VBP2Growth,
    subscriptionName: 'vbp2_growth_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-b6607aae-606f-4fd5-84e4-c48f38b59892',
    name: WholesalePricingPlanName.VBP2Scale,
    subscriptionName: 'vbp2_scale_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-7375d567-8890-4cb1-930c-5673ea89acba',
    name: WholesalePricingPlanName.VBP2Premium,
    subscriptionName: 'vbp2_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-38a06671-526b-4d28-b425-bbce3be34699',
    name: WholesalePricingPlanName.BasicSubscription,
    subscriptionName: 'basic_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-d9717300-f1c3-4471-8433-c3ac7baf003d',
    name: WholesalePricingPlanName.ProfessionalSubscription,
    subscriptionName: 'professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-f6a60130-878b-4a33-9a10-d5bdeac0cdef',
    name: WholesalePricingPlanName.EnterpriseSubscription,
    subscriptionName: 'enterprise_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-dff68124-2223-4c88-91dc-b638a3135fc5',
    name: WholesalePricingPlanName.EliteSubscription,
    subscriptionName: 'elite_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-15641015-560e-4cd7-977b-b1fc627b18d1',
    name: WholesalePricingPlanName.PremiumSubscription,
    subscriptionName: 'premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-46fe3624-0af5-4674-b612-2c39d7dc1f82',
    name: WholesalePricingPlanName.IndividualSubscription,
    subscriptionName: 'm3_solo_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-8153423f-af6e-459e-ac9b-ac10e8f789b6',
    name: WholesalePricingPlanName.TeamSubscription,
    subscriptionName: 'm3_small_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-54de0158-18c3-4dca-bcac-a26e945ac8c0',
    name: WholesalePricingPlanName.AdvanceSubscription,
    subscriptionName: 'm3_medium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-f81375af-43a0-4986-9b90-e56c67922195',
    name: WholesalePricingPlanName.CustomSubscription,
    subscriptionName: 'm3_large_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-589c64da-e354-4103-bf21-26596bf0fb66',
    name: WholesalePricingPlanName.Essentials2022Subscription,
    subscriptionName: 'vbp_2022_essentials_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-528f7285-8040-4432-a4ca-9e53e12f903c',
    name: WholesalePricingPlanName.Professional2022Subscription,
    subscriptionName: 'vbp_2022_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-aceec94d-e714-4a77-b45c-133d9ce788a6',
    name: WholesalePricingPlanName.Premium2022Subscription,
    subscriptionName: 'vbp_2022_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-0a9ff4cf-6135-4994-add3-8cd02c7da795',
    name: WholesalePricingPlanName.Custom2022Subscription,
    subscriptionName: 'vbp_2022_custom_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-c8810795-4d7c-4194-a551-e7f654fca4d7',
    name: WholesalePricingPlanName.Starter2023Subscription,
    subscriptionName: 'vbp_2023_starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-a8a291ad-32cf-490e-bf64-de0a1bf73caf',
    name: WholesalePricingPlanName.Starter2024Subscription,
    subscriptionName: 'vbp_2024_starter_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-d75f33d2-a518-4c46-9c44-349704bef4a9',
    name: WholesalePricingPlanName.Professional2024Subscription,
    subscriptionName: 'vbp_2024_professional_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-3d445c79-1b71-4cc7-a3ec-83d1cfbc5d06',
    name: WholesalePricingPlanName.Premium2024Subscription,
    subscriptionName: 'vbp_2024_premium_subscription',
    defaultStrategy: BillingStrategy.Invalid,
  },
  {
    id: 'PP-0e9ac148-a2d0-42d0-9250-8c42189bf432',
    name: WholesalePricingPlanName.Default,
    subscriptionName: '',
    defaultStrategy: BillingStrategy.Invalid,
  },
];

const pricingPlans = new Map<Environment, PricingPlan[]>([
  [Environment.DEMO, [...demoWholesalePricingPlans, ...demoRetailPricingPlans]],
  [Environment.PROD, [...prodWholesalePricingPlans, ...prodRetailPricingPlans]],
]);

const wholesalePlans = new Map<Environment, PricingPlan[]>([
  [Environment.DEMO, [...demoWholesalePricingPlans]],
  [Environment.PROD, [...prodWholesalePricingPlans]],
]);

const retailPlans = new Map<Environment, PricingPlan[]>([
  [Environment.DEMO, [...demoRetailPricingPlans]],
  [Environment.PROD, [...prodRetailPricingPlans]],
]);
